/**
 * Renders Toast only when `notification.scopes` and `layout.scopes` (Redux) intersect.
 * Each shell screen should call `layoutActions.setScopes([...])` on mount and clear on unmount.
 * Mounted separately so the layout does not re-render on every notification.
 *
 * After the toast was actually shown (`visible` was true) and it stops being visible (e.g. user
 * navigates so scopes no longer match), Redux is cleared so the same message cannot reappear as a
 * ghost when returning to a matching route. Notifications that never became visible (e.g. navigate
 * before first paint) are not cleared here, so the destination can still show them once scopes match.
 *
 * Portal to `document.body`: avoids legacy styles (`#root > div:first-child` in `_animate.scss` / `_app.scss`,
 * e.g. `overflow: auto`, height rules) clipping or affecting the Toast’s `position: fixed`.
 *
 * **Warning** and **error** toasts stay visible until the user closes them (no auto-close).
 * **Success** and **info** follow Redux `autoDismiss` (auto-close by default).
 * The primary action button is not used here; only the dashboard maintenance toast passes `onButtonClick`.
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { arrayOf, func, number, string } from "prop-types";
import { connect } from "react-redux";
import { Toast } from "@ui-kit/components/index.es";

import { actions as notificationActions, selectors as notificationSelectors } from "reducers/notification";
import { selectors as layoutSelectors } from "reducers/layout";

const LEVEL_TO_VARIANT = {
    error: "error",
    warning: "warning",
    success: "success",
    info: "info",
};

const DEFAULT_AUTO_CLOSE_DELAY = 5000;

function hasScopeMatch(notificationScopes, layoutScopes) {
    if (!Array.isArray(notificationScopes) || !Array.isArray(layoutScopes)) {
        return false;
    }
    return notificationScopes.some((s) => layoutScopes.includes(s));
}

function ToastNotification({ dispatch, layoutScopes, message, level, notificationScopes, title, topOffsetPx }) {
    const scopeMatch = hasScopeMatch(notificationScopes, layoutScopes);
    const visible = !!message && scopeMatch;

    const prevVisibleRef = useRef(false);

    useEffect(() => {
        const wasVisible = prevVisibleRef.current;
        if (wasVisible && !visible && message) {
            dispatch(notificationActions.removeNotification());
        }
        prevVisibleRef.current = visible;
    }, [dispatch, message, visible]);

    const handleClose = () => {
        dispatch(notificationActions.removeNotification());
    };

    const variant = LEVEL_TO_VARIANT[level] ?? "info";
    const isPersistentVariant = level === "error" || level === "warning";
    const autoClose = !isPersistentVariant;

    if (!visible || typeof document === "undefined" || !document.body) {
        return null;
    }

    return createPortal(
        <Toast
            variant={variant}
            message={message}
            title={title}
            visible={visible}
            onClose={handleClose}
            autoClose={autoClose}
            autoCloseDelay={DEFAULT_AUTO_CLOSE_DELAY}
            topOffsetPx={topOffsetPx}
        />,
        document.body,
    );
}

ToastNotification.displayName = "ToastNotification";

ToastNotification.propTypes = {
    dispatch: func.isRequired,
    layoutScopes: arrayOf(string).isRequired,
    message: string,
    level: string,
    notificationScopes: arrayOf(string).isRequired,
    title: string,
    topOffsetPx: number,
};

ToastNotification.defaultProps = {
    message: null,
    level: null,
    title: null,
    topOffsetPx: undefined,
};

const mapStateToProps = (state) => ({
    layoutScopes: layoutSelectors.getScopes(state),
    message: notificationSelectors.getMessage(state),
    level: notificationSelectors.getLevel(state),
    notificationScopes: notificationSelectors.getScopes(state),
    title: notificationSelectors.getTitle(state),
});

export default connect(mapStateToProps)(ToastNotification);
